import { useRequestApi } from '@/common/_hooks/useRequestApi'
import { ApiResponse } from '@/common/_types/message'
import '@/app/assets/css/map.css'
import { Box, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { oilResourceMap } from '../_types/OilPrice'
import { TOilPrc } from '../_types/apiResponse'
import { mapBgGroup, mapData } from '../_types/map'

type ComponentProps = {
  title: string
  descriptions: string[]
}
// YYYYMMDD 형식을 YYYY년 MM월 DD일 형식으로 변환
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return ''
  const year = dateString.substring(0, 4)
  const month = dateString.substring(4, 6)
  const day = dateString.substring(6, 8)
  return `${year}년 ${month}월 ${day}일`
}

const OilMapSection = ({ title, descriptions }: ComponentProps) => {
  const { sendSimpleHttpRequest } = useRequestApi()
  const [rgnCd, setRgnCd] = useState<string>('11000')
  const [dateInfo, setDateInfo] = useState('')
  const [tOilPrc, setTOilPrc] = useState<{
    [rgnCd: string]: { [koiCd: string]: TOilPrc }
  }>({})

  useEffect(() => {
    const totalData = tOilPrc['00000']
    if (!totalData) return

    const firstKey = Object.keys(oilResourceMap)[0]
    const firstData = totalData[firstKey]
    if (!firstData?.aplcnBgngYmd) return

    const formattedDateString = formatDate(firstData.aplcnBgngYmd)
    if (dateInfo !== formattedDateString) {
      setDateInfo(formattedDateString)
    }
  }, [tOilPrc])

  const getPriceItem = (
    koiCd: string,
    totalData: TOilPrc,
    regeionData: TOilPrc,
  ) => {
    const resource = oilResourceMap[koiCd]
    if (totalData) {
      const formattedDateString = formatDate(totalData.aplcnBgngYmd)
    }
    return (
      <Box className="oilps-map-info-box" key={koiCd}>
        <Box className="oilps-info-title" style={resource.style}>
          {resource.name}
          <br />({resource.unit})
        </Box>
        <Box className="oilps-info-con">
          <Typography className="oilps-info-con-title">전국평균유가</Typography>
          {totalData && (
            <Typography className="oilps-info-con-value textR">
              {totalData.avgPrice.toLocaleString('ko-KR')}
              <Box className="info-value-small" component={'span'}>
                원
              </Box>
            </Typography>
          )}
          {!totalData && (
            <Typography className="oilps-info-con-value info-value-small">
              정보가 없습니다.
            </Typography>
          )}
        </Box>
        <Box className="oilps-info-con">
          <Typography className="oilps-info-con-title">
            지역별평균유가
          </Typography>
          {regeionData && (
            <Typography className="oilps-info-con-value textB">
              {regeionData.avgPrice.toLocaleString('ko-KR')}
              <Box className="info-value-small" component={'span'}>
                원
              </Box>
            </Typography>
          )}
          {!regeionData && (
            <Typography className="oilps-info-con-value info-value-small">
              정보가 없습니다.
            </Typography>
          )}
        </Box>
      </Box>
    )
  }
  const getPrices = () => {
    const totalData = tOilPrc['00000']
    const rgnData = tOilPrc[rgnCd]
    return (
      <Box className="oilps-map-info-box-group">
        {Object.entries(oilResourceMap).map(([key, value]) => {
          return getPriceItem(
            key,
            totalData?.[key] ?? undefined,
            rgnData?.[key] ?? undefined,
          )
        })}
      </Box>
    )
  }
  const getDescription = (index: number, description: string) => (
    <Box className="contents-explanation marB10" key={index}>
      <Box className="contents-explanation-inner">
        <Box className="contents-explanation-text">{description}</Box>
      </Box>
    </Box>
  )
  useEffect(() => {
    ;(async () => {
      try {
        const response: ApiResponse = await sendSimpleHttpRequest(
          'GET',
          '/pbc/taoi/rus',
        )
        if (response && response.resultType === 'success' && response.data) {
          // 데이터 조회 성공시
          setTOilPrc(response.data)
        } else {
          // 데이터가 없거나 실패
          setTOilPrc({})
        }
      } catch (error) {
        // 에러시
        console.error('Error fetching data:', error)
      }
    })()
  }, [])
  return (
    <Box className="main-contents-box">
      <Typography className="contents-box-title" variant="h1">
        {title}
      </Typography>
      <Box className="contents-box-con">
        <Box className="oilps-map-info-group">
          <Box className="oilps-map-info-col">
            <Typography className="oilps-info-date">
              {mapData[rgnCd].name} {dateInfo}
            </Typography>
            {getPrices()}
          </Box>

          <Box className="oilps-map-info-col">
            <Box className="oilps-map-wrapper">
              <Box className="oilps-map-inner">
                <Box className="oilps-map-point">
                  {Object.entries(mapData).map(([key, value]) => {
                    let className = 'map-point'
                    if (key === rgnCd) className += ' active'
                    return (
                      <Box
                        key={key}
                        tabIndex={0}
                        onFocus={() => {
                          setRgnCd(key)
                        }}
                        className={className}
                        style={{
                          cursor: 'default',
                          ...value.style,
                        }}
                        onMouseEnter={(
                          event: React.MouseEvent<HTMLDivElement>,
                        ) => {
                          // 이름 hover 에 우선순위 주기 위헤 stopPropagation 호출
                          event.preventDefault()
                          event.stopPropagation()
                          setRgnCd(key)
                        }}
                      >
                        <span className="map-point-text">{value.name}</span>
                        <span className="map-point-icon"></span>
                      </Box>
                    )
                  })}
                </Box>
                <Box className="oilps-map-bg">
                  <svg className="map-svg" viewBox="0 0 849.89 1059.49">
                    {
                      <>
                        {mapBgGroup as React.ReactNode}
                        {Object.entries(mapData).map(([key, value]) => {
                          return (
                            <g
                              key={key}
                              onMouseEnter={(
                                event: React.MouseEvent<SVGElement>,
                              ) => {
                                if (event.currentTarget === event.target) {
                                  event.preventDefault()
                                  setRgnCd(key)
                                }
                              }}
                            >
                              {value.pathGroup}
                            </g>
                          )
                        })}
                      </>
                    }
                  </svg>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {descriptions.map((description, index) =>
          getDescription(index, description),
        )}
      </Box>
    </Box>
  )
}

export default OilMapSection
